import AntList from "antd/lib/list";
import clsx from "clsx";
import { forwardRef, useMemo, useCallback, useEffect, useRef } from "react";

import { Checkbox } from "@sol/uikit/data-entry/Checkbox";
import { Radio } from "@sol/uikit/data-entry/Radio";

import { INTERACTIVE_ELEMENTS_SELECTOR } from "./constants";
import { ListItemRenderProps } from "./List.types";
import { useInteractiveNavigation } from "./useInteractiveNavigation";

const { Item } = AntList;

type ListItemDomProps = Omit<
    React.HTMLAttributes<HTMLDivElement>,
    keyof ListItemRenderProps | "className" | "children"
>;

export interface ListItemProps extends ListItemRenderProps, ListItemDomProps {
    rootClassName?: string;
    className?: string;
    children: React.ReactNode;
    align?: "start" | "center" | "end";
}

/**
 * List item component with WAI-ARIA support (role="row" > role="gridcell")
 */

export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
    (
        {
            rootClassName,
            className,
            selected,
            selectionMode,
            showSelectionIndicator,
            disabled = false,
            children,
            tabIndex,
            onClick,
            onKeyDown,
            onFocus,
            id,
            focused = false,
            isInInteractionMode = false,
            align = "start",
            ...restProps
        },
        ref,
    ) => {
        const hasSelection = Boolean(selectionMode);
        const shouldShowIndicator = hasSelection && showSelectionIndicator;
        const SelectionControl = selectionMode === "single" ? Radio : Checkbox;

        const interactiveNavRef = useInteractiveNavigation({ isActive: isInInteractionMode });
        const cellRef = useRef<HTMLDivElement | null>(null);

        const combinedRef = useCallback(
            (element: HTMLDivElement | null) => {
                cellRef.current = element;
                interactiveNavRef.current = element;
                if (typeof ref === "function") {
                    ref(element);
                } else if (ref) {
                    ref.current = element;
                }
            },
            [ref, interactiveNavRef],
        );

        // Manage tabindex of interactive elements based on mode
        useEffect(() => {
            if (!cellRef.current) {
                return;
            }

            const elements = cellRef.current.querySelectorAll<HTMLElement>(INTERACTIVE_ELEMENTS_SELECTOR);

            elements.forEach(el => {
                if (isInInteractionMode) {
                    // Restore original or remove
                    const original = el.getAttribute("data-original-tabindex");
                    if (original === "null" || !original) {
                        el.removeAttribute("tabindex");
                    } else {
                        el.setAttribute("tabindex", original);
                    }
                    el.removeAttribute("data-original-tabindex");
                } else {
                    // Save and disable
                    el.setAttribute("data-original-tabindex", el.getAttribute("tabindex") ?? "null");
                    el.setAttribute("tabindex", "-1");
                }
            });
        }, [isInInteractionMode, children]);

        const contentWrapperClasses = useMemo(
            () =>
                clsx(
                    "min-w-0 flex-1 rounded-ant-lg transition-all duration-200 ease-in-out",
                    "border-ant border-ant-transparent focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-white",
                    {
                        "cursor-pointer": hasSelection && !disabled,
                        "cursor-not-allowed opacity-70 ": disabled,
                        "border-ant-primary! bg-ant-primary": hasSelection && selected,
                        "hover:bg-ant-primary-hover": !disabled && !selected,
                        "hover:bg-ant-primary": hasSelection && !disabled && selected,
                    },
                    className,
                ),
            [hasSelection, selected, disabled, className],
        );

        const cellAriaProps = useMemo(
            () => ({
                "aria-selected": hasSelection ? Boolean(selected) : undefined,
                "aria-disabled": disabled || undefined,
                "data-focused": focused || undefined,
            }),
            [hasSelection, selected, disabled, focused],
        );

        return (
            <Item
                role="row"
                className={clsx(
                    "flex! gap-ant-sm rounded-none p-0! outline-hidden",
                    align === "start" && "items-start!",
                    align === "center" && "items-center!",
                    align === "end" && "items-end!",
                    rootClassName,
                )}
            >
                {shouldShowIndicator && (
                    <div
                        role="gridcell"
                        className={clsx("shrink-0", align === "start" && "pt-ant-sm", align === "end" && "pb-ant-sm")}
                    >
                        <SelectionControl
                            checked={selected ?? false}
                            disabled={disabled}
                            tabIndex={-1}
                            onChange={() => {
                                onClick?.();
                            }}
                        />
                    </div>
                )}

                <div
                    {...restProps}
                    {...cellAriaProps}
                    role="gridcell"
                    ref={combinedRef}
                    id={id}
                    className={contentWrapperClasses}
                    tabIndex={tabIndex}
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                >
                    {children}
                </div>
            </Item>
        );
    },
);

ListItem.displayName = "ListItem";
